import React, { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { Box } from '@wix/design-system';
import s from './EmailVerificationResult.scss';
import { DialogHeader } from '../DialogHeader/DialogHeader';
import { DialogContainer } from '../DialogContainer';
import { dataHooks } from '../../dataHooks';
import { AppContextProvider } from '../AppLoader';
import { ThemedText } from '../ThemedComponents';
import { SubmitButton } from '../SubmitButton';

export const EmailVerificationResult: React.FC = observer(() => {
  const {
    rootStore: { emailVerificationResultStore },
  } = useContext(AppContextProvider);
  const { isSuccess, isCrossAccount, isExpiredOrInvalid } =
    emailVerificationResultStore;

  return (
    <Box
      direction="vertical"
      width="100%"
      align="center"
      className={s.pageWrapper}
    >
      <DialogContainer
        dataHook={dataHooks.emailVerificationResult.container}
        mainHeaderProps={{ showBackButton: false }}
        className={s.emailVerificationResultContainer}
      >
        {isSuccess && <SuccessState />}
        {isCrossAccount && <CrossAccountState />}
        {isExpiredOrInvalid && <ExpiredState />}
      </DialogContainer>
    </Box>
  );
});

const SuccessState: React.FC = observer(() => {
  const { t } = useTranslation();
  const {
    rootStore: { emailVerificationResultStore },
  } = useContext(AppContextProvider);
  return (
    <>
      <DialogHeader>
        <DialogHeader.Title flow="secondaryFlow">
          {t('verifyEmailPage.success.title')}
        </DialogHeader.Title>
        <DialogHeader.SubTitleSmall
          weight="normal"
          size="medium"
          maxWidth="440px"
          className={s.descriptionText}
        >
          {t('verifyEmailPage.success.content')}
        </DialogHeader.SubTitleSmall>
      </DialogHeader>
      <Box align="center" width="100%" className={s.ctaButton}>
        <SubmitButton
          skin="standard"
          dataHook={dataHooks.emailVerificationResult.homeButton}
          onClick={emailVerificationResultStore.onGoHome}
        >
          {t('verifyEmailPage.success.cta')}
        </SubmitButton>
      </Box>
    </>
  );
});

const ExpiredState: React.FC = observer(() => {
  const { t } = useTranslation();
  const {
    rootStore: {
      emailVerificationResultStore,
      displayStore: { isMobile },
    },
  } = useContext(AppContextProvider);
  return (
    <>
      <DialogHeader>
        <DialogHeader.Title flow="secondaryFlow">
          {t('verifyEmailPage.expired.title')}
        </DialogHeader.Title>
        {isMobile && (
          <DialogHeader.SubTitleSmall
            weight="normal"
            size="medium"
            maxWidth="320px"
            className={s.descriptionText}
          >
            {t('verifyEmailPage.expired.mobileContent')}
          </DialogHeader.SubTitleSmall>
        )}
      </DialogHeader>
      <Box align="center" width="100%" className={s.ctaButton}>
        <SubmitButton
          skin="standard"
          dataHook={dataHooks.emailVerificationResult.resendButton}
          onClick={emailVerificationResultStore.onResend}
        >
          {t(
            isMobile
              ? 'verifyEmailPage.expired.mobileCta'
              : 'verifyEmailPage.expired.cta',
          )}
        </SubmitButton>
      </Box>
    </>
  );
});

const CrossAccountState: React.FC = observer(() => {
  const { t } = useTranslation();
  const {
    rootStore: { emailVerificationResultStore },
  } = useContext(AppContextProvider);
  const { tokenEmail, sessionEmail } = emailVerificationResultStore;
  return (
    <>
      <DialogHeader>
        <DialogHeader.Title flow="secondaryFlow">
          {t('verifyEmailPage.crossAccount.title', { tokenEmail })}
        </DialogHeader.Title>
        <DialogHeader.SubTitleSmall
          weight="normal"
          size="medium"
          maxWidth="440px"
          className={s.descriptionText}
        >
          <Trans
            i18nKey="verifyEmailPage.crossAccount.content"
            values={{ sessionEmail, tokenEmail }}
            components={[
              <ThemedText weight="bold" />,
              <ThemedText weight="bold" />,
            ]}
          />
        </DialogHeader.SubTitleSmall>
      </DialogHeader>
      <Box align="center" width="100%" className={s.ctaButton}>
        <SubmitButton
          skin="standard"
          dataHook={dataHooks.emailVerificationResult.loginButton}
          onClick={emailVerificationResultStore.onCrossAccountLogin}
        >
          {t('verifyEmailPage.crossAccount.cta', { tokenEmail })}
        </SubmitButton>
      </Box>
    </>
  );
});
